import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { X, Download, ChevronLeft, ChevronRight, Calendar, Users, BarChart3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SESSION_COLORS = {
  FS: 'bg-blue-100 text-blue-800',
  HS1: 'bg-green-100 text-green-800',
  HS2: 'bg-purple-100 text-purple-800',
  '6WK': 'bg-orange-100 text-orange-800',
  FLEX: 'bg-pink-100 text-pink-800',
  CIT: 'bg-gray-200 text-gray-800'
};

function StatusDot({ present, cap }) {
  const pct = cap > 0 ? (present / cap) * 100 : 0;
  const color = pct >= 100 ? 'bg-red-500' : pct >= 85 ? 'bg-yellow-500' : 'bg-green-500';
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`} />;
}

function availColor(available, cap) {
  if (available <= 0) return 'bg-red-200 text-red-900';
  if (available <= Math.ceil(cap * 0.15)) return 'bg-yellow-100 text-yellow-900';
  return 'bg-green-100 text-green-900';
}

export function SnapshotBar({ report, selectedDay, onDayChange }) {
  if (!report) return null;

  const { session_totals, session_caps, calendar, total_campers } = report;
  const dayData = calendar?.[selectedDay - 1];

  return (
    <div className="bg-white border-b border-gray-200 px-4 py-2 sticky top-0 z-30" data-testid="snapshot-bar">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {Object.entries(session_totals).map(([st, count]) => (
            <div key={st} className="flex items-center gap-1">
              <Badge variant="outline" className={`text-xs font-semibold ${SESSION_COLORS[st] || ''}`}>
                {st}: {count}
              </Badge>
            </div>
          ))}
          <Badge variant="outline" className="text-xs bg-gray-100 text-gray-700 font-semibold">
            Total: {total_campers}
          </Badge>
        </div>

        <div className="flex items-center gap-3 ml-auto">
          {dayData && (
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-xs font-semibold text-gray-700">
                {dayData.total_present} campers on camp
              </span>
              <div className="flex gap-1 ml-1">
                {Object.entries(dayData.session_breakdown).filter(([,v]) => v > 0).map(([st, count]) => (
                  <span key={st} className={`text-[10px] px-1 rounded ${SESSION_COLORS[st]}`}>
                    {st}:{count}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="w-px h-6 bg-gray-300 hidden md:block" />

          <div className="flex items-center gap-1" data-testid="day-selector">
            <button
              onClick={() => onDayChange(Math.max(1, selectedDay - 1))}
              disabled={selectedDay <= 1}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
              data-testid="day-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <select
              value={selectedDay}
              onChange={(e) => onDayChange(Number(e.target.value))}
              className="text-xs border rounded px-2 py-1 bg-white font-medium min-w-[160px]"
              data-testid="day-select"
            >
              {calendar?.map((d) => (
                <option key={d.day_num} value={d.day_num}>
                  Day {d.day_num} — {d.display} ({d.weekday})
                </option>
              ))}
            </select>
            <button
              onClick={() => onDayChange(Math.min(38, selectedDay + 1))}
              disabled={selectedDay >= 38}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
              data-testid="day-next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function GroupAvailabilityBadge({ report, groupCode, selectedDay }) {
  if (!report || !report.calendar) return null;
  const dayData = report.calendar[selectedDay - 1];
  if (!dayData) return null;
  const gd = dayData.groups?.[groupCode];
  if (!gd) return null;

  return (
    <div className="flex items-center gap-1.5 text-xs" data-testid={`group-avail-${groupCode}`}>
      <StatusDot present={gd.present} cap={gd.cap} />
      <span className="font-medium">Day {selectedDay}: {gd.present}/{gd.cap}</span>
      <span className="text-gray-500">
        ({gd.available >= 0 ? gd.available : 0} avail)
      </span>
    </div>
  );
}

export function GroupSessionBreakdown({ campers }) {
  const counts = useMemo(() => {
    const c = { FS: 0, HS1: 0, HS2: 0, '6WK': 0, FLEX: 0 };
    campers.forEach(cam => {
      const st = classifySession(cam.session);
      c[st] = (c[st] || 0) + 1;
    });
    return c;
  }, [campers]);

  return (
    <div className="flex gap-1 flex-wrap">
      {Object.entries(counts).filter(([,v]) => v > 0).map(([st, count]) => (
        <span key={st} className={`text-[10px] px-1 py-0.5 rounded font-medium ${SESSION_COLORS[st]}`}>
          {st}:{count}
        </span>
      ))}
    </div>
  );
}

function classifySession(sessionStr) {
  if (!sessionStr) return 'FS';
  const s = sessionStr.toLowerCase();
  if (s.includes('half season 1') || s.includes('half 1')) return 'HS1';
  if (s.includes('half season 2') || s.includes('half 2')) return 'HS2';
  if (s.includes('6 week')) return '6WK';
  if (s.includes('flex')) return 'FLEX';
  return 'FS';
}

export function ReportModal({ isOpen, onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      fetch(`${API_URL}/api/availability/report`)
        .then(r => r.json())
        .then(data => { setReport(data); setLoading(false); })
        .catch(() => setLoading(false));
    }
  }, [isOpen]);

  const handleDownloadCSV = async () => {
    try {
      const res = await fetch(`${API_URL}/api/availability/export/csv`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `availability_report_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('CSV download failed:', e);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="report-modal">
      <div className="bg-white rounded-xl shadow-2xl w-[97vw] h-[94vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-[#1a3a6b] text-white rounded-t-xl">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            <h2 className="text-lg font-bold">2026 Rolling River Group Availability</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={handleDownloadCSV} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="report-download-csv">
              <Download className="w-4 h-4 mr-1" /> Download CSV
            </Button>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="report-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full text-gray-500">Loading report...</div>
          ) : report ? (
            <ReportTable report={report} />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">No data</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReportTable({ report }) {
  const { calendar, groups } = report;

  return (
    <div className="text-[10px] leading-tight">
      <table className="border-collapse">
        {/* Header Row 1: Day numbers spanning 3 cols each */}
        <thead>
          <tr className="bg-[#1a3a6b] text-white">
            <th className="border border-[#2b5ea7] px-1 py-1 text-left sticky top-0 left-0 bg-[#1a3a6b] z-40 min-w-[30px]">#</th>
            <th className="border border-[#2b5ea7] px-1 py-1 text-left sticky top-0 left-[30px] bg-[#1a3a6b] z-40 min-w-[110px]">Camp Group</th>
            {calendar.map(d => (
              <th key={d.day_num} colSpan={3} className="border border-[#2b5ea7] px-0 py-1 text-center min-w-[72px] sticky top-0 bg-[#1a3a6b] z-30 border-l-2 border-l-black">
                <div className="font-bold">Day {d.day_num}</div>
                <div className="font-normal text-blue-200 text-[9px]">{d.display} {d.weekday}</div>
              </th>
            ))}
          </tr>
          {/* Sub-header: Enr | Avail | Cap for each day */}
          <tr className="bg-[#2b5ea7] text-white text-[9px]">
            <th className="border border-[#3b6eb7] sticky top-[38px] left-0 bg-[#2b5ea7] z-40"></th>
            <th className="border border-[#3b6eb7] sticky top-[38px] left-[30px] bg-[#2b5ea7] z-40"></th>
            {calendar.map(d => (
              <React.Fragment key={d.day_num}>
                <th className="border border-[#3b6eb7] px-0.5 py-0.5 text-center w-[24px] sticky top-[38px] bg-[#2b5ea7] z-30 border-l-2 border-l-black">Enr</th>
                <th className="border border-[#3b6eb7] px-0.5 py-0.5 text-center w-[24px] sticky top-[38px] bg-[#2b5ea7] z-30">Avl</th>
                <th className="border border-[#3b6eb7] px-0.5 py-0.5 text-center w-[24px] sticky top-[38px] bg-[#2b5ea7] z-30">Cap</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Total row at top */}
          <tr className="bg-gray-800 text-white font-bold">
            <td className="border border-gray-600 px-1 py-0.5 sticky left-0 bg-gray-800 z-10"></td>
            <td className="border border-gray-600 px-1 py-0.5 sticky left-[30px] bg-gray-800 z-10">TOTAL</td>
            {calendar.map(d => {
              const totalCap = groups.reduce((sum, g) => sum + (d.groups[g.code]?.cap || 0), 0);
              const totalPresent = d.total_present;
              const totalAvail = totalCap - totalPresent;
              return (
                <React.Fragment key={d.day_num}>
                  <td className="border border-gray-600 px-0.5 py-0.5 text-center border-l-2 border-l-black">{totalPresent}</td>
                  <td className="border border-gray-600 px-0.5 py-0.5 text-center">{totalAvail}</td>
                  <td className="border border-gray-600 px-0.5 py-0.5 text-center">{totalCap}</td>
                </React.Fragment>
              );
            })}
          </tr>

          {/* Group rows */}
          {groups.map((g, gi) => {
            const grade = g.code.startsWith('N') ? '' : g.code.startsWith('B') || g.code.startsWith('G') ? g.code.replace(/[BG]/, '').replace(/^0/, '') : '';
            return (
              <tr key={g.code} className={gi % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="border px-1 py-0.5 text-center sticky left-0 bg-inherit z-10 text-gray-500 font-medium">
                  {grade}
                </td>
                <td className="border px-1 py-0.5 font-semibold sticky left-[30px] bg-inherit z-10 whitespace-nowrap">
                  {g.display}
                </td>
                {calendar.map(d => {
                  const gd = d.groups[g.code] || {};
                  const present = gd.present || 0;
                  const cap = gd.cap || 0;
                  const avail = cap - present;
                  const ac = availColor(avail, cap);
                  return (
                    <React.Fragment key={d.day_num}>
                      <td className={`border px-0.5 py-0.5 text-center font-medium ${ac} border-l-2 border-l-black`}>{present}</td>
                      <td className={`border px-0.5 py-0.5 text-center ${ac}`}>{avail}</td>
                      <td className="border px-0.5 py-0.5 text-center text-gray-500">{cap}</td>
                    </React.Fragment>
                  );
                })}
              </tr>
            );
          })}

          {/* Bottom total row */}
          <tr className="bg-gray-800 text-white font-bold">
            <td className="border border-gray-600 px-1 py-0.5 sticky left-0 bg-gray-800 z-10"></td>
            <td className="border border-gray-600 px-1 py-0.5 sticky left-[30px] bg-gray-800 z-10">TOTAL</td>
            {calendar.map(d => {
              const totalCap = groups.reduce((sum, g) => sum + (d.groups[g.code]?.cap || 0), 0);
              const totalPresent = d.total_present;
              const totalAvail = totalCap - totalPresent;
              return (
                <React.Fragment key={d.day_num}>
                  <td className="border border-gray-600 px-0.5 py-0.5 text-center border-l-2 border-l-black">{totalPresent}</td>
                  <td className="border border-gray-600 px-0.5 py-0.5 text-center">{totalAvail}</td>
                  <td className="border border-gray-600 px-0.5 py-0.5 text-center">{totalCap}</td>
                </React.Fragment>
              );
            })}
          </tr>

          {/* Session breakdown */}
          <tr><td colSpan={2 + 38 * 3} className="h-4 border-0" /></tr>
          <tr className="bg-gray-200 font-bold">
            <td className="border px-1 py-1 sticky left-0 bg-gray-200 z-10" colSpan={2}>Session Breakdown by Day</td>
            {calendar.map(d => (
              <td key={d.day_num} colSpan={3} className="border px-0.5 py-1 text-center text-[9px] text-gray-600">D{d.day_num}</td>
            ))}
          </tr>
          {['FS', 'HS1', 'HS2', '6WK', 'FLEX', 'CIT'].map(st => (
            <tr key={st} className="bg-gray-50">
              <td className="border px-1 py-0.5 sticky left-0 bg-gray-50 z-10" colSpan={2}>
                <Badge variant="outline" className={`text-[9px] ${SESSION_COLORS[st]}`}>{st}</Badge>
              </td>
              {calendar.map(d => (
                <td key={d.day_num} colSpan={3} className="border px-0.5 py-0.5 text-center">
                  {d.session_breakdown[st] || 0}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ReportModal;
